import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Users } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import {
  fetchCompanyDashboardAgePyramidDetail,
  type AgePyramidDetail,
} from "../api/companyApi";
import {
  enterpriseDashboardPathWithPeriod,
  parseDashboardPeriodSearchParams,
} from "../lib/dashboardPeriodQuery";

const KIND_LABELS: Record<string, string> = {
  employee: "Agent",
  spouse: "Conjoint",
  child: "Enfant",
};

function fmtDateFr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
}

export function EnterpriseDashboardAgePyramidDetailPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.enterpriseAnalytics.agePyramid;
  const [searchParams] = useSearchParams();
  const filters = useMemo(
    () => parseDashboardPeriodSearchParams(searchParams),
    [searchParams],
  );
  const backHref = enterpriseDashboardPathWithPeriod(filters);

  const [periodLabel, setPeriodLabel] = useState("");
  const [data, setData] = useState<AgePyramidDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchCompanyDashboardAgePyramidDetail({
      date_start: filters.dateFrom || undefined,
      date_end: filters.dateTo || undefined,
      hospital_id: filters.hospitalId ? Number(filters.hospitalId) : undefined,
    })
      .then((response) => {
        if (cancelled) return;
        setPeriodLabel(response.period_label);
        setData(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          sanitizeApiErrorMessage(
            err instanceof Error ? err.message : "Impossible de charger le détail.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.dateFrom, filters.dateTo, filters.hospitalId]);

  const asOfLabel = data?.as_of ? fmtDateFr(data.as_of) : fmtDateFr(filters.dateTo || null);
  const withBirthdate = Number(data?.total_with_birthdate ?? 0);
  const withoutBirthdate = Number(data?.total_without_birthdate ?? 0);

  return (
    <div className="space-y-5">
      <Link
        to={backHref}
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={16} />
        {labels.back}
      </Link>

      <DashboardPageHeader
        title={labels.title}
        subtitle={periodLabel || labels.subtitleFallback}
        icon={Users}
      />

      {error ? (
        <p className="rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted">{labels.loading}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {(data?.buckets ?? []).map((bucket) => (
              <div key={bucket.label} className="theme-card p-4 text-center">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {bucket.label}
                </p>
                <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{bucket.count}</p>
                <p className="mt-1 text-xs text-muted">
                  {Number(bucket.pct ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
            {labels.summary
              .replace("{date}", asOfLabel)
              .replace("{withBirthdate}", String(withBirthdate))
              .replace("{withoutBirthdate}", String(withoutBirthdate))}
          </div>

          <div className="theme-card overflow-x-auto p-0">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border bg-card-muted text-left text-[11px] uppercase tracking-wide text-muted">
                  <th className="px-4 py-3">{labels.columns.beneficiary}</th>
                  <th className="px-4 py-3">{labels.columns.type}</th>
                  <th className="px-4 py-3">{labels.columns.employee}</th>
                  <th className="px-4 py-3">{labels.columns.birthdate}</th>
                  <th className="px-4 py-3">{labels.columns.age}</th>
                  <th className="px-4 py-3">{labels.columns.bucket}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.beneficiaries ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted">
                      {labels.empty}
                    </td>
                  </tr>
                ) : (
                  (data?.beneficiaries ?? []).map((row, index) => (
                    <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{row.name ?? "—"}</td>
                      <td className="px-4 py-3">{KIND_LABELS[row.kind] ?? row.kind}</td>
                      <td className="px-4 py-3">{row.employee_name ?? "—"}</td>
                      <td className="px-4 py-3">{fmtDateFr(row.birthdate)}</td>
                      <td className="px-4 py-3">
                        {row.age != null ? `${row.age} ans` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {row.bucket ?? "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
